import { Button, Checkbox, Divider, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
  CustomAvatar,
  FormProvider,
  GlassEffectDialog,
  RHFTextField,
  Scrollbar,
} from '@app/components';
import { IAddTaskBoardSchema, IMember } from '../types';
import { useRequestState } from '@app/hooks';
import { selectBoardDetailsState, selectGetLabelsState } from '../selector';
import { SvgColor } from '@app/components/svg-color';
import { Strings } from '@app/constants';
import { selectMyProfileFormState } from '@app/modules/my-profile';
import { getLabels } from '../slice';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  filters: {
    overdue: boolean;
    unassigned: boolean;
    noDate: boolean;
    assignedToMe: boolean;
    noLabel: boolean;
    selectedMember: any;
    selectedLabel: any;
    searchKeyword: any;
  };
  setFilters: (filters: any) => void;
}

const FilterDialog = ({ open, onClose, filters, setFilters }: Props) => {
  const methods = useForm<IAddTaskBoardSchema>();
  const dispatch = useDispatch();

  const { data } = useRequestState({
    stateSelector: selectBoardDetailsState,
    errorShown: true,
  });
  const { data: myProfile } = useRequestState({
    stateSelector: selectMyProfileFormState,
    errorShown: true,
  });
  const theme = useTheme();

  const { data: boardDetails } = useRequestState({
    stateSelector: selectBoardDetailsState,
    errorShown: true,
  });

  const handleFilterChange =
    (filterName: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = event.target as HTMLInputElement;
      const value = target.type === 'checkbox' ? target.checked : target.value;

      setFilters((prevFilters: any) => {
        // eslint-disable-next-line prefer-const
        let updatedFilters = { ...prevFilters };
        if (
          filterName === 'overdue' ||
          filterName === 'unassigned' ||
          filterName === 'noDate' ||
          filterName === 'assignedToMe' ||
          filterName === 'noLabel'
        ) {
          updatedFilters[filterName] = value;
        } else if (filterName === 'selectedMember' || filterName === 'selectedLabel') {
          updatedFilters[filterName] = value;
        } else if (filterName === 'searchKeyword') {
          updatedFilters[filterName] = value;
        }

        return updatedFilters;
      });
    };

  const membersOptions = boardDetails?.members?.map((member: IMember) => ({
    value: member.membersUserId,
    label: `${member.firstName} ${member.lastName}`,
  }));

  useEffect(() => {
    dispatch(getLabels({ boardId: data?.id }));
  }, [dispatch]);

  const { data: labels } = useRequestState({
    stateSelector: selectGetLabelsState,
    errorShown: true,
  });

  const uniqueLabels =
    labels &&
    labels?.length > 0 &&
    labels.filter(
      (item: any, index: number, self: any) =>
        index === self.findIndex((t: any) => t.name === item.name && t.color === item.color)
    );

  return (
    <>
      <FormProvider methods={methods}>
        <GlassEffectDialog
          open={open}
          onClose={onClose}
          title={'Filter'}
          backgroundImage={data?.image}
          height="100vh"
          boardIcon="ic_filter"
        >
          <Divider sx={{ mt: -3, mb: 1 }} />
          <Scrollbar sx={{ height: 450 }}>
            <Stack mb={2} mr={1}>
              <Stack alignItems={'flex-end'}>
                <Button
                  sx={{ width: 100 }}
                  onClick={() => {
                    setFilters('');
                  }}
                  variant="contained"
                  color="inherit"
                >
                  {Strings.button.clear}
                </Button>
              </Stack>
              <Stack sx={{ width: '400px' }}>
                <Typography variant="h6" color={theme.palette.taskboard.raisinBlack}>
                  {Strings.taskBoard.keyword}
                </Typography>
                <RHFTextField
                  name="search"
                  size="small"
                  label={'Search Task Name.....'}
                  defaultValue={filters.searchKeyword}
                  sx={{
                    top: 5,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme.palette.taskboard.raisinBlack,
                      },
                    },
                  }}
                  InputLabelProps={{
                    style: { color: theme.palette.taskboard.raisinBlack, fontSize: 12 },
                  }}
                  onChange={handleFilterChange('searchKeyword')}
                />
              </Stack>
              <Stack mt={1}>
                <Typography variant="h6" color={theme.palette.taskboard.raisinBlack}>
                  {Strings.taskBoard.assignee}
                </Typography>
                <Stack direction={'row'}>
                  <Checkbox
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: theme.palette.taskboard.raisinBlack,
                      },
                    }}
                    onChange={handleFilterChange('unassigned')}
                    checked={filters.unassigned}
                  />
                  <Stack
                    height={20}
                    width={20}
                    bgcolor={'#C0C0C0'}
                    borderRadius={'50%'}
                    justifyContent={'center'}
                    mt={0.8}
                    mr={1}
                  >
                    <SvgColor
                      src={`/assets/icons/card/ic_unassigned.svg`}
                      sx={{
                        width: '13px',
                        height: '13px',
                        color: theme.palette.taskboard.raisinBlack,
                        alignSelf: 'center',
                      }}
                    />
                  </Stack>
                  <Typography mt={0.8} variant="body2">
                    {Strings.taskBoard.unAssigned}
                  </Typography>
                </Stack>
                <Stack direction={'row'}>
                  <Checkbox
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: theme.palette.taskboard.raisinBlack,
                      },
                    }}
                    onChange={handleFilterChange('assignedToMe')}
                    checked={filters.assignedToMe}
                  />
                  <CustomAvatar
                    name={myProfile?.firstName}
                    src={myProfile?.profileImage}
                    sx={{
                      width: 20,
                      height: 20,
                      alignSelf: 'center',
                      backgroundColor: theme.palette.taskboard.darkSlateGray,
                      color: theme.palette.common.white,
                      fontSize: 10,
                      fontWeight: 600,
                      mr: 1,
                    }}
                  />
                  <Typography mt={0.8} variant="body2">
                    {Strings.taskBoard.cardsAssignedToMe}
                  </Typography>
                </Stack>
                <Stack direction={'row'}>
                  <Checkbox
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: theme.palette.taskboard.raisinBlack,
                      },
                    }}
                    checked={filters?.selectedMember ?? false}
                    onChange={handleFilterChange('selectedMember')}
                  />
                  <RHFTextField
                    name="members"
                    size="small"
                    label="Select Members...."
                    value={filters.selectedMember || ''}
                    select
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme.palette.taskboard.raisinBlack,
                        },
                      },
                    }}
                    InputLabelProps={{
                      style: { color: theme.palette.taskboard.raisinBlack, fontSize: 12 },
                    }}
                    onChange={handleFilterChange('selectedMember')}
                  >
                    {membersOptions.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </RHFTextField>
                </Stack>
              </Stack>
              <Stack mt={1}>
                <Typography variant="h6" color={theme.palette.taskboard.raisinBlack}>
                  {Strings.dashboard.dueDate}
                </Typography>
                <Stack direction={'row'}>
                  <Checkbox
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: theme.palette.taskboard.raisinBlack,
                      },
                    }}
                    checked={filters.noDate}
                    onChange={handleFilterChange('noDate')}
                  />
                  <Stack
                    height={20}
                    width={20}
                    bgcolor={'#C0C0C0'}
                    borderRadius={'50%'}
                    justifyContent={'center'}
                    mt={0.8}
                    mr={1}
                  >
                    <SvgColor
                      src={`/assets/icons/card/ic_date.svg`}
                      sx={{
                        width: '13px',
                        height: '13px',
                        color: theme.palette.taskboard.raisinBlack,
                        alignSelf: 'center',
                      }}
                    />
                  </Stack>
                  <Typography mt={0.8} variant="body2">
                    {Strings.taskBoard.noDate}
                  </Typography>
                </Stack>
                <Stack direction={'row'}>
                  <Checkbox
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: theme.palette.taskboard.raisinBlack,
                      },
                    }}
                    onChange={handleFilterChange('overdue')}
                    checked={filters.overdue}
                  />
                  <Stack
                    height={20}
                    width={20}
                    bgcolor={'#C13030'}
                    borderRadius={'50%'}
                    justifyContent={'center'}
                    mt={0.8}
                    mr={1}
                  >
                    <SvgColor
                      src={`/assets/icons/card/ic_overdue.svg`}
                      sx={{
                        width: '10px',
                        height: '10px',
                        color: 'white',
                        alignSelf: 'center',
                      }}
                    />
                  </Stack>
                  <Typography mt={0.8} variant="body2">
                    {Strings.projects.overdue}
                  </Typography>
                </Stack>
              </Stack>
              <Stack mt={1}>
                <Typography variant="h6" color={theme.palette.taskboard.raisinBlack}>
                  {Strings.taskBoard.labels}
                </Typography>
                <Stack direction={'row'}>
                  <Checkbox
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: theme.palette.taskboard.raisinBlack,
                      },
                    }}
                    onChange={handleFilterChange('noLabel')}
                    checked={filters.noLabel}
                  />
                  <Stack
                    height={20}
                    width={20}
                    bgcolor={'#C0C0C0'}
                    borderRadius={'50%'}
                    justifyContent={'center'}
                    mt={0.8}
                    mr={1}
                  >
                    <SvgColor
                      src={`/assets/icons/card/ic_label.svg`}
                      sx={{
                        width: '13px',
                        height: '13px',
                        color: theme.palette.taskboard.raisinBlack,
                        alignSelf: 'center',
                      }}
                    />
                  </Stack>
                  <Typography mt={0.8} variant="body2">
                    {Strings.taskBoard.noLabel}
                  </Typography>
                </Stack>
                <Stack direction={'row'} style={{ top: 10 }}>
                  <Checkbox
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: theme.palette.taskboard.raisinBlack,
                      },
                    }}
                    checked={filters?.selectedLabel ?? false}
                    onChange={handleFilterChange('selectedLabel')}
                  />
                  <RHFTextField
                    name="labels"
                    size="small"
                    label="Select label"
                    select
                    sx={{
                      top: 5,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme.palette.taskboard.raisinBlack,
                        },
                      },
                    }}
                    InputLabelProps={{
                      style: { color: theme.palette.taskboard.raisinBlack, fontSize: 12 },
                    }}
                    onChange={handleFilterChange('selectedLabel')}
                    value={filters.selectedLabel || ''}
                  >
                    {uniqueLabels &&
                      uniqueLabels?.length > 0 &&
                      uniqueLabels?.map((option: any) => (
                        <MenuItem key={option?.id} value={option?.color}>
                          <Stack
                            sx={{
                              height: '25px',
                              width: '70%',
                              borderRadius: '4px',
                              backgroundColor: option.color,
                              justifyContent: 'center',
                              mt: 1,
                            }}
                            pl={2}
                          >
                            {option?.name}
                          </Stack>
                        </MenuItem>
                      ))}
                  </RHFTextField>
                </Stack>
              </Stack>
            </Stack>
          </Scrollbar>
        </GlassEffectDialog>
      </FormProvider>
    </>
  );
};
export { FilterDialog };
